import {Component, Object3D, ViewComponent} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {AxisNegZ, Mathf} from '../utils/Mathf.js';
import {Puzzle, PuzzleConstants} from './puzzle.js';
import {mat4, quat, vec2, vec3} from 'gl-matrix';
import {InputManager, KeyEventType, KeyType} from '../classes/InputManager.js';
import {BoardObject} from './board-object.js';
import {CameraShakeParameters} from '../classes/CameraShakeParameters.js';
import {Easing, lerp} from '../utils/lerp.js';
import {TimeManager} from './time-manager.js';
import {GameFlowController} from './game-flow-controller.js';
import {isMobile} from '../utils/mobile.js';

const tempQuat = quat.create();

const tempVec3 = vec3.create();
const tempVec3_2 = vec3.create();
const tempVec3_3 = vec3.create();

const tempVec2 = vec2.create();
const tempVec2Direction = vec2.create();
const tempVec2Delta = vec2.create();

export enum DropMode {
    Normal,
    Immediate,
}

/**
 * This component is an interop component that help with values from the camera.
 * It needs to be placed on the same object as the camera.
 */
export class PuzzleCamera extends Component {
    static TypeName = 'puzzle-camera';

    @property.float(90)
    rotationDegreesPerInch = 90;

    @property.float(70)
    desktopRotationDegreesPerInch = 70;

    @property.object({required: true})
    lookAt!: Object3D;

    @property.float(180)
    initialAngle = 180;

    @property.object({required: true})
    puzzleObject!: Object3D;

    @property.float(0.4)
    clickRadiusInches = 0.4;

    @property.int(10)
    swipeDetectHistorySize = 10;
    @property.float(0.8)
    swipeDownAngleAcceptThreshold = 0.8;
    @property.float(0.03)
    swipeDownDistanceAcceptThreshold = 0.03;

    @property.object({required: true})
    cameraObject!: Object3D;

    currentDropMode: DropMode = DropMode.Normal;
    dropImmediateAllowed: boolean = true;
    allowRotation = true;

    private _puzzle: Puzzle;
    private _input: InputManager;

    private _lastTouchPosition: vec2 = vec2.create();
    private _initialTouchPosition: vec2 = vec2.create();
    private _angle = 0;

    public get angle() {
        return this._angle;
    }
    public set angle(value) {
        if (this._angle == value) return;
        this._smoothRotationTime = 1;
        this._angle = value;
    }

    private _radius = 0;
    private _transformYBase = 0;

    private _leftClickThreshold: boolean;
    private _clickThreshold2: number = 0.0;
    private _lastPressedObj: BoardObject;
    private _origin = new Float32Array(3);
    private _direction = new Float32Array(3);
    //private _lastPointerPos = new Float32Array(3);
    private _projectionMatrix = new Float32Array(16);
    private _viewComponent: ViewComponent;
    private _rotationDegreesPerPixel;

    private _currentSwipe: vec2 = vec2.create();
    private _recordTop: number = 0;
    private _positionRecord: vec2[] = [];
    private _swipeDownDistance2: number;
    private _minimumDelta2: number;

    private _shakeParams: CameraShakeParameters;
    private _shakesLeft = 0;
    private _shakeLast = 0;
    private _shakeTime = 0;
    private _shakeSeconds = 0;
    private _shakeVerticalOffset = 0;
    private _shakeHorizontalOffset = 0;

    private _lanes: Object3D[];
    private _degreesPerLane?: number = null;
    private _maxLane: number = -1;
    private _minLane: number = -1;
    private _time = 0;
    private _smoothRotationTime: number = 0;
    private _targetAngle: number;
    private _sourceAngle: number;

    start() {
        this._puzzle = this.puzzleObject.getComponent(Puzzle);
        this._viewComponent = this.cameraObject.getComponent(ViewComponent);

        this.object.getPositionWorld(tempVec3);
        this.lookAt.getPositionWorld(tempVec3_2);
        vec3.subtract(tempVec3_3, tempVec3, tempVec3_2);
        tempVec3_3[1] = 0.0;
        this._radius = vec3.len(tempVec3_3);
        this._angle = this.initialAngle;
        this._transformYBase = tempVec3[1];

        this._updatePosition();

        this._lanes = this._puzzle.lanes;
        this._degreesPerLane = 360.0 / PuzzleConstants.columnCount;

        const swipeDownDistance =
            this.swipeDownDistanceAcceptThreshold * this.engine.canvas.height;
        this._swipeDownDistance2 = swipeDownDistance ** 2;
        const minimumDelta = swipeDownDistance / this.swipeDetectHistorySize;
        this._minimumDelta2 = minimumDelta ** 2;

        const dpi = (window.devicePixelRatio ?? 1) * 96;

        if (isMobile) {
            this._rotationDegreesPerPixel = this.rotationDegreesPerInch / dpi;
        } else {
            this._rotationDegreesPerPixel = this.desktopRotationDegreesPerInch / dpi;
        }

        const clickThreshold = this.clickRadiusInches * dpi;
        this._clickThreshold2 = clickThreshold ** 2;

        this._puzzle.registerResetGameCallback(this._resetGame);
        this._clearSwipeHistory();
    }

    update(dt: number) {
        if (TimeManager.instance.paused && !GameFlowController.instance.tutorialRunning) {
            // game is paused
            return;
        }

        this._time += dt;
        if (!this._puzzle.gameInProgress) {
            if (this._shakeParams != null && this._shakesLeft >= 0) {
                this._updatePosition();
            }
            return;
        }
        this.currentDropMode = DropMode.Normal;

        this._input = InputManager.instance;

        if (this._input.getTouchTriggered(0)) {
            this._clearSwipeHistory();

            vec2.copy(this._initialTouchPosition, this._input.getTouchPoint(0));
            vec2.copy(this._lastTouchPosition, this._initialTouchPosition);
            this._lastPressedObj = null;
            this._leftClickThreshold = false;
        } else if (this._input.getTouchPressed(0)) {
            this._recordSwipeHistory(this._input.getTouchPoint(0));

            vec2.subtract(
                tempVec2Delta,
                this._lastTouchPosition,
                this._input.getTouchPoint(0)
            );
            if (
                this.allowRotation &&
                Math.abs(tempVec2Delta[0]) > Math.abs(tempVec2Delta[1])
            ) {
                // && Time.timeScale > 0.0f)
                this._angle += tempVec2Delta[0] * this._rotationDegreesPerPixel;
                this.angleClamp();
            }

            vec2.sub(tempVec2, this._input.getTouchPoint(0), this._initialTouchPosition);
            if (
                !this._leftClickThreshold &&
                vec2.sqrLen(tempVec2) > this._clickThreshold2
            ) {
                this._leftClickThreshold = true;
            }

            const obj = this._findTouchedBlob();
            if (obj && obj != this._lastPressedObj) {
                // press!
                obj.press();
                this._lastPressedObj = obj;
            }

            vec2.copy(this._lastTouchPosition, this._input.getTouchPoint(0));
        } else if (this._input.getTouchReleased(0)) {
            vec2.subtract(
                tempVec2,
                this._input.getTouchPoint(0),
                this._initialTouchPosition
            );
            if (
                !this._leftClickThreshold &&
                vec2.sqrLen(tempVec2) < this._clickThreshold2
            ) {
                // it's a click
                const obj = this._findTouchedBlob();
                if (obj) {
                    // explode!
                    obj.click();
                }
            } else if (this.dropImmediateAllowed) {
                vec2.normalize(tempVec2Direction, this._currentSwipe);
                if (
                    tempVec2Direction[1] >= this.swipeDownAngleAcceptThreshold &&
                    vec2.squaredLength(this._currentSwipe) >= this._swipeDownDistance2
                ) {
                    // it was mostly down, drop it!
                    this.currentDropMode = DropMode.Immediate;
                }
            }
        }

        if (
            this._input.getKeyStatus(KeyType.Down) == KeyEventType.Triggered ||
            this._input.getKeyStatus(KeyType.Button1) == KeyEventType.Triggered
        ) {
            if (this.dropImmediateAllowed) {
                this.currentDropMode = DropMode.Immediate;
            }
        }
        if (this._input.getKeyStatus(KeyType.Left) == KeyEventType.Triggered) {
            if (this.allowRotation && this._smoothRotationTime <= 0.0) {
                const previousLane = this.findClosestLane() - 1;
                this._targetAngle = previousLane * this._degreesPerLane;
                this._sourceAngle = this._angle;

                this._smoothRotationTime = 1;
            }
        }
        if (this._input.getKeyStatus(KeyType.Right) == KeyEventType.Triggered) {
            if (this.allowRotation && this._smoothRotationTime <= 0.0) {
                const nextLane = this.findClosestLane() + 1;
                this._targetAngle = nextLane * this._degreesPerLane;
                this._sourceAngle = this._angle;

                this._smoothRotationTime = 1;
            }
        }
        if (this._smoothRotationTime > 0.0) {
            this._smoothRotationTime -= dt / 0.1;
            if (this._smoothRotationTime <= 0.0) {
                this._angle = Mathf.repeat(this._targetAngle, 360.0);
                this.angleClamp();
            } else {
                this._angle = Mathf.repeat(
                    lerp(
                        this._sourceAngle,
                        this._targetAngle,
                        1 - this._smoothRotationTime,
                        Easing.InOutCubic
                    ),
                    360.0
                );
            }
        }
        this._updatePosition();
    }

    onActivate(): void {
        this.engine.onResize.add(this._onViewportResize);
        this._onViewportResize();
        TimeManager.instance.onPaused.add(this._onPaused);
        TimeManager.instance.onResumed.add(this._onResumed);
    }

    onDeactivate(): void {
        this.engine.onResize.remove(this._onViewportResize);
        TimeManager.instance.onPaused.remove(this._onPaused);
        TimeManager.instance.onResumed.remove(this._onResumed);
    }

    findClosestLane() {
        this.object.getPositionWorld(tempVec3);
        tempVec3[1] = 0;
        vec3.normalize(tempVec3, tempVec3);
        const pDot = vec3.dot(tempVec3, AxisNegZ);
        vec3.cross(tempVec3_2, AxisNegZ, tempVec3);
        let angle = Math.acos(pDot) * Mathf.Rad2Deg;
        const dLine = 360 / this._puzzle.lanes.length;

        if (tempVec3_2[1] < 0) {
            angle = 360 - angle;
        }
        // calculate the lane number, shift by half a lane to make the
        // lane line up with the camera in the middle
        angle = (angle + 180 + dLine / 2) % 360;
        let laneNum = ~~(angle / dLine);
        return laneNum;
    }

    angleClamp() {
        this._angle = Mathf.repeat(this._angle, 360.0);
        const lane = this.findClosestLane();

        // We need to check to see if the new lane is less than the min or greater
        // than the max, but sometimes we can jump several lanes in one swipe.  Check
        // a reasonable number of lanes but not so many that the user might wrap.
        const lanesToCheck = this._lanes.length - 2;
        const lanesWrap = this._lanes.length;
        if (this._minLane != -1 && this._maxLane != -1) {
            // if both minLane and maxLane are set, they should be the same lane
            if (this._minLane == this._maxLane && lane != this._minLane) {
                // check to see what side of the angle we should clamp to by
                // looking at which one is closer
                const center = this._minLane * this._degreesPerLane;
                const halfWidth = this._degreesPerLane / 2.0;
                const min = Math.abs(Mathf.repeat(center - halfWidth, 360.0));
                const max = Math.abs(Mathf.repeat(center + halfWidth, 360.0));
                let direction;
                if (Math.abs(this._angle - min) < Math.abs(this._angle - max)) {
                    direction = -1;
                } else {
                    direction = 1;
                }
                this._angle = Mathf.repeat(center + halfWidth * direction, 360.0);
            }
        } else if (this._minLane != -1 && lane != this._minLane) {
            let change = false;
            for (let i = 0; i < lanesToCheck; i++) {
                const laneCheck = (this._minLane - i + lanesWrap) % lanesWrap;
                if (laneCheck == lane) {
                    change = true;
                    break;
                }
            }
            if (change) {
                this._angle = Mathf.repeat(
                    this._minLane * this._degreesPerLane - this._degreesPerLane / 2,
                    360
                );
            }
        } else if (this._maxLane != -1 && lane != this._maxLane) {
            let change = false;
            for (let i = 0; i < lanesToCheck; i++) {
                let laneCheck = (this._maxLane + i) % lanesWrap;
                if (laneCheck == lane) {
                    change = true;
                    break;
                }
            }
            if (change) {
                this._angle = Mathf.repeat(
                    this._maxLane * this._degreesPerLane + this._degreesPerLane / 2,
                    360
                );
            }
        }
    }

    setMinLane(lane: number) {
        this._minLane = lane;
    }

    setMaxLane(lane: number) {
        this._maxLane = lane;
    }

    angleForLane(lane: number): number {
        return (this._angle = Mathf.repeat(
            lane * this._degreesPerLane, // + this._degreesPerLane / 2,
            360
        ));
    }

    private _recordSwipeHistory(pisition: vec2) {
        vec2.subtract(tempVec2, pisition, this._lastTouchPosition);
        if (vec2.sqrLen(tempVec2) >= this._minimumDelta2) {
            vec2.subtract(
                this._currentSwipe,
                this._currentSwipe,
                this._positionRecord[this._recordTop]
            );
            vec2.add(this._currentSwipe, this._currentSwipe, tempVec2);
            vec2.copy(this._positionRecord[this._recordTop], tempVec2);

            this._recordTop = (this._recordTop + 1) % this._positionRecord.length;
        }
    }

    private _updateDirection() {
        const bounds = this.engine.canvas!.getBoundingClientRect();
        /* Get direction in normalized device coordinate space from mouse position */
        const left = this._lastTouchPosition[0] / bounds.width;
        const top = this._lastTouchPosition[1] / bounds.height;
        this._direction[0] = left * 2 - 1;
        this._direction[1] = -top * 2 + 1;
        this._direction[2] = -1.0;

        this._applyTransformAndProjectDirection();
    }

    private _applyTransformAndProjectDirection() {
        /* Reverse-project the direction into view space */
        vec3.transformMat4(this._direction, this._direction, this._projectionMatrix);
        vec3.normalize(this._direction, this._direction);
        this._applyTransformToDirection();
    }

    private _applyTransformToDirection() {
        this.object.transformVectorWorld(this._direction, this._direction);
        this.object.getPositionWorld(this._origin);
    }

    private _findTouchedBlob(): BoardObject {
        this._onViewportResize();
        const obj = this._rayCast();
        if (obj) {
            return obj.getComponent(BoardObject);
        }
        return null;
    }

    private _rayCast() {
        const rayHit = this.engine.scene.rayCast(this._origin, this._direction, 255);
        // this.engine.physics!.rayCast(
        //     this._origin,
        //     this._direction,
        //     255,
        //     100
        // );

        if (rayHit.hitCount > 0) {
            return rayHit.objects[0];
        } else return null;

        // let hitResultDistance = Infinity;
        // let hitTestResult = null;
        // if (this._hitTestLocation?.visible) {
        //     this._hitTestObject!.getPositionWorld(this.cursorPos);
        //     hitResultDistance = vec3.distance(
        //         this.object.getPositionWorld(tempVec),
        //         this.cursorPos
        //     );

        //     hitTestResult = this._hitTestLocation?.getHitTestResults(frame)[0];
        // }

        // let hoveringReality = false;
        // if (rayHit.hitCount > 0) {
        //     const d = rayHit.distances[0];
        //     if (hitResultDistance >= d) {
        //         /* Override cursorPos set by hit test location */
        //         this.cursorPos.set(rayHit.locations[0]);
        //     } else {
        //         hoveringReality = true;
        //     }
        // } else if (hitResultDistance < Infinity) {
        //     /* cursorPos already set */
        // } else {
        //     this.cursorPos.fill(0);
        // }

        // if (hoveringReality && !this.hoveringReality) {
        //     this.hitTestTarget.onHover.notify(hitTestResult, this);
        // } else if (!hoveringReality && this.hoveringReality) {
        //     this.hitTestTarget.onUnhover.notify(hitTestResult, this);
        // }
        // this.hoveringReality = hoveringReality;

        // this.hoverBehaviour(rayHit, hitTestResult, doClick, originalEvent);

        //return rayHit;
    }

    private _onViewportResize = () => {
        if (!this._viewComponent) return;
        /* Projection matrix will change if the viewport is resized, which will affect the
         * projection matrix because of the aspect ratio. */
        mat4.invert(this._projectionMatrix, this._viewComponent.projectionMatrix);
    };

    private _clearSwipeHistory() {
        vec2.zero(this._currentSwipe);
        this._recordTop = 0;
        this._positionRecord.length = 0;
        for (let x = 0; x < this.swipeDetectHistorySize; x++) {
            this._positionRecord.push(vec2.create());
        }
    }

    private _resetGame = (puzzle: Puzzle) => {
        this._angle = this.initialAngle;
        this._updatePosition();
    };

    private _updatePosition() {
        this.object.getPositionWorld(tempVec3);
        let angleOffset = 0;

        if (this._shakeParams != null && this._shakesLeft >= 0) {
            if (this._shakeLast == 0 || this._shakeSeconds > this._shakeTime) {
                --this._shakesLeft;
                this._shakeTime = this._shakeParams.timePerShakeInSeconds / 2;
                this._shakeSeconds = 0;
                if ((this._shakesLeft & 1) == 0) {
                    this._shakeVerticalOffset =
                        Math.random() * this._shakeParams.verticalMagnitudeInDegrees;
                    this._shakeHorizontalOffset =
                        Math.random() * this._shakeParams.horizontalMagnitudeInDegrees;
                }
            }
            let bump;
            if ((this._shakesLeft & 1) != 0) {
                bump = this._shakeSeconds / this._shakeTime;
            } else {
                bump = (this._shakeTime - this._shakeSeconds) / this._shakeTime;
            }
            const lerped = lerp(0, 1, bump);
            angleOffset = this._shakeHorizontalOffset * lerped;

            tempVec3[1] =
                this._transformYBase +
                Math.sin(this._shakeVerticalOffset * lerped * Mathf.Deg2Rad) * this._radius;
            this._shakeSeconds += this._time - this._shakeLast;
            this._shakeLast = this._time;
        } else {
            tempVec3[1] = this._transformYBase;
        }

        const newAngle = Mathf.repeat(this._angle + angleOffset, 360.0) * Mathf.Deg2Rad;
        tempVec3[0] = Math.sin(newAngle) * this._radius;
        tempVec3[2] = Math.cos(newAngle) * this._radius;
        this.object.setPositionWorld(tempVec3);
        this.lookAt.getPositionWorld(tempVec3_2);
        this.object.lookAt(tempVec3_2);

        this._updateDirection();
    }

    shakeCamera(newParams: CameraShakeParameters) {
        if (newParams != null && newParams.count != 0) {
            this._shakeParams = new CameraShakeParameters(
                newParams.verticalMagnitudeInDegrees,
                newParams.horizontalMagnitudeInDegrees,
                newParams.timePerShakeInSeconds,
                newParams.count
            );

            this._shakesLeft = this._shakeParams.count * 2;
            this._shakeLast = 0;
            this._shakeTime = 0;
            this._shakeSeconds = 0;
            this._shakeVerticalOffset = 0;
            this._shakeHorizontalOffset = 0;
        }
    }

    private _onPaused = () => {};
    private _onResumed = () => {
        this._lastPressedObj = null;
        this._leftClickThreshold = false;
        this._clearSwipeHistory();
        this._updatePosition();
    };
}
